const STORAGE_KEY = "successor-session-expires-at";
const RESET_EVENT = "successor-session-reset";

export const SUCCESSOR_SESSION_DURATION_MS = 5 * 60 * 1000;

const safeNow = () => Date.now();

const parseMs = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

export const getSuccessorSessionExpiresAt = (): number | null => {
  try {
    return parseMs(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const setSuccessorSessionExpiresAt = (expiresAtMs: number) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(expiresAtMs));
  } catch {
    // ignore (private mode / storage blocked)
  }

  // Inform listeners in this tab (no cross-tab needed for sessionStorage)
  window.dispatchEvent(new CustomEvent(RESET_EVENT, { detail: { expiresAtMs } }));
};

export const resetSuccessorSessionTimer = () => {
  const expiresAtMs = safeNow() + SUCCESSOR_SESSION_DURATION_MS;
  setSuccessorSessionExpiresAt(expiresAtMs);
  return expiresAtMs;
};

export const clearSuccessorSessionTimer = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(RESET_EVENT, { detail: { expiresAtMs: null } }));
};

export const getSuccessorSessionRemainingMs = (): number => {
  const expiresAt = getSuccessorSessionExpiresAt();
  if (!expiresAt) return 0;
  return Math.max(0, expiresAt - safeNow());
};

export const addSuccessorSessionTimerListener = (
  handler: (expiresAtMs: number | null) => void
) => {
  const listener = (e: Event) => {
    const ce = e as CustomEvent;
    handler((ce.detail?.expiresAtMs as number | null) ?? null);
  };

  window.addEventListener(RESET_EVENT, listener);

  return () => window.removeEventListener(RESET_EVENT, listener);
};

export const isSuccessorSessionResetEndpoint = (url?: string) => {
  if (!url) return false;

  // url in axios can be relative ("/successors/..."), sometimes without leading slash
  const normalized = url.startsWith("/") ? url : `/${url}`;

  if (!normalized.startsWith("/successors")) return false;
  if (normalized.startsWith("/successors/logout")) return false;

  return true;
};
